/**
 * auto-stop-lib.ts — Hàm thuần cho hộp "Tự động ngừng trả lời" (auto-stop-dialog.tsx). Có test riêng.
 * Khớp backend `ecdp_backend_source/src/modules/ai/bot-settings.ts` (normAutoStop) — backend là nơi kiểm chặt.
 */
import type { AutoStopSchedule, AutoStopSettings, Condition, ConditionOp } from './bot-settings'
import type { CatalogVariable, VariableCatalog } from './train-api'
import { i18n } from '@/lib/i18n'

export const AUTO_STOP_LIMITS = { groups: 10, conditionsPerGroup: 10, schedules: 14, images: 3, closing: 500 } as const
export const DEFAULT_TZ = 'Asia/Ho_Chi_Minh'

export const STOP_OP_LABELS: Record<ConditionOp, string> = {
  get '='() { return i18n.t('aiTrainLib:autoStopLib.op.eq') },
  get '!='() { return i18n.t('aiTrainLib:autoStopLib.op.neq') },
  get in() { return i18n.t('aiTrainLib:autoStopLib.op.in') },
  get not_in() { return i18n.t('aiTrainLib:autoStopLib.op.notIn') },
  get is_null() { return i18n.t('aiTrainLib:autoStopLib.op.isNull') },
  get is_not_null() { return i18n.t('aiTrainLib:autoStopLib.op.isNotNull') },
  get '>'() { return i18n.t('aiTrainLib:autoStopLib.op.gt') },
  get '<'() { return i18n.t('aiTrainLib:autoStopLib.op.lt') },
  get '>='() { return i18n.t('aiTrainLib:autoStopLib.op.gte') },
  get '<='() { return i18n.t('aiTrainLib:autoStopLib.op.lte') },
}
/** Giải thích phép so sánh (ⓘ trong bảng chọn). */
export const STOP_OP_HINTS: Partial<Record<ConditionOp, string>> = {
  get in() { return i18n.t('aiTrainLib:autoStopLib.hint.in') },
  get not_in() { return i18n.t('aiTrainLib:autoStopLib.hint.notIn') },
  get is_null() { return i18n.t('aiTrainLib:autoStopLib.hint.isNull') },
  get is_not_null() { return i18n.t('aiTrainLib:autoStopLib.hint.isNotNull') },
}

export const WEEKDAY_NAMES: Array<{ value: number; label: string; short: string }> = [
  { value: 1, get label() { return i18n.t('aiTrainLib:autoStopLib.weekday.d1.label') }, get short() { return i18n.t('aiTrainLib:autoStopLib.weekday.d1.short') } }, { value: 2, get label() { return i18n.t('aiTrainLib:autoStopLib.weekday.d2.label') }, get short() { return i18n.t('aiTrainLib:autoStopLib.weekday.d2.short') } }, { value: 3, get label() { return i18n.t('aiTrainLib:autoStopLib.weekday.d3.label') }, get short() { return i18n.t('aiTrainLib:autoStopLib.weekday.d3.short') } },
  { value: 4, get label() { return i18n.t('aiTrainLib:autoStopLib.weekday.d4.label') }, get short() { return i18n.t('aiTrainLib:autoStopLib.weekday.d4.short') } }, { value: 5, get label() { return i18n.t('aiTrainLib:autoStopLib.weekday.d5.label') }, get short() { return i18n.t('aiTrainLib:autoStopLib.weekday.d5.short') } }, { value: 6, get label() { return i18n.t('aiTrainLib:autoStopLib.weekday.d6.label') }, get short() { return i18n.t('aiTrainLib:autoStopLib.weekday.d6.short') } },
  { value: 0, get label() { return i18n.t('aiTrainLib:autoStopLib.weekday.d0.label') }, get short() { return i18n.t('aiTrainLib:autoStopLib.weekday.d0.short') } },
]
export const REPEAT_OPTIONS: Array<{ value: AutoStopSchedule['repeat']; label: string }> = [
  { value: 'weekly', get label() { return i18n.t('aiTrainLib:autoStopLib.repeat.weekly') } },
  { value: 'daily', get label() { return i18n.t('aiTrainLib:autoStopLib.repeat.daily') } },
  { value: 'dates', get label() { return i18n.t('aiTrainLib:autoStopLib.repeat.dates') } },
]

// ── Bảng chọn biến ───────────────────────────────────────────────────────────

export type PickerSectionId = 'customer' | 'custom' | 'system'
export type PickerSection = { id: PickerSectionId; label: string; hint: string; variables: CatalogVariable[] }

/** 3 nhóm của cột "Truy vấn": khách hàng (trường cố định) · tuỳ chỉnh (thuộc tính tổ chức + AI thu thập) · hệ thống. */
export function buildPickerSections(cat: VariableCatalog | undefined): PickerSection[] {
  const groups = cat?.groups ?? []
  const vars = (id: string) => (groups.find((g) => g.id === id)?.variables ?? []).filter((v) => v.conditionable !== false)
  const customer = vars('customer')
  return [
    { id: 'customer', label: i18n.t('aiTrainLib:autoStopLib.picker.customer.label'), hint: i18n.t('aiTrainLib:autoStopLib.picker.customer.hint'), variables: customer.filter((v) => !v.custom) },
    {
      id: 'custom', label: i18n.t('aiTrainLib:autoStopLib.picker.custom.label'), hint: i18n.t('aiTrainLib:autoStopLib.picker.custom.hint'),
      variables: [...customer.filter((v) => v.custom), ...vars('agent').map((v) => ({ ...v, description: v.description ?? i18n.t('aiTrainLib:autoStopLib.picker.agentDescription') }))],
    },
    { id: 'system', label: i18n.t('aiTrainLib:autoStopLib.picker.system.label'), hint: i18n.t('aiTrainLib:autoStopLib.picker.system.hint'), variables: vars('system') },
  ]
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().trim() // i18n-ignore

/** Lọc theo ô "Tìm nội dung" — không phân biệt dấu / hoa thường. */
export function searchVariables(list: CatalogVariable[], q: string): CatalogVariable[] {
  const k = fold(q)
  return k ? list.filter((v) => fold(v.name).includes(k) || v.code.includes(k)) : list
}

export type ValueKind = 'none' | 'number' | 'text' | 'select' | 'multi-select' | 'tags'

/** Ô nhập giá trị hợp với biến + phép so sánh. */
export function valueKind(v: Pick<CatalogVariable, 'type' | 'options'> | undefined, op: ConditionOp): ValueKind {
  if (op === 'is_null' || op === 'is_not_null') return 'none'
  const hasOpts = (v?.options.length ?? 0) > 0
  if (op === 'in' || op === 'not_in') return hasOpts ? 'multi-select' : 'tags'
  if (op === '>' || op === '<' || op === '>=' || op === '<=' || v?.type === 'number') return 'number'
  return hasOpts ? 'select' : 'text'
}

/** Giá trị mặc định khi đổi phép so sánh (giữ được phần còn dùng được). */
export function coerceValue(kind: ValueKind, prev: Condition['value']): Condition['value'] {
  if (kind === 'none') return undefined
  if (kind === 'multi-select' || kind === 'tags') return Array.isArray(prev) ? prev : prev != null && prev !== '' ? [String(prev)] : []
  const single = Array.isArray(prev) ? prev[0] ?? '' : prev ?? ''
  if (kind === 'number') return single === '' || !Number.isFinite(Number(single)) ? '' : Number(single)
  return String(single)
}

/** Chip điều kiện: "Tên · Phép so sánh · Giá trị". */
export function conditionChip(c: Condition, v: CatalogVariable | undefined): string[] {
  const label = (x: string) => v?.options.find((o) => o.value === x)?.label ?? x
  const val = c.value == null || c.value === '' ? '' : Array.isArray(c.value) ? c.value.map(label).join(', ') : label(String(c.value))
  return [v?.name ?? c.variable, STOP_OP_LABELS[c.op] ?? c.op, ...(val ? [val] : [])]
}

/** Điều kiện còn thiếu gì (null = hợp lệ). */
export function stopConditionProblem(c: Condition): string | null {
  if (!c.variable) return i18n.t('aiTrainLib:autoStopLib.cond.pickVar')
  if (c.op === 'is_null' || c.op === 'is_not_null') return null
  if (c.op === 'in' || c.op === 'not_in') return Array.isArray(c.value) && c.value.length ? null : i18n.t('aiTrainLib:autoStopLib.cond.pickValue')
  if (c.op === '>' || c.op === '<' || c.op === '>=' || c.op === '<=') {
    return c.value !== '' && c.value != null && Number.isFinite(Number(c.value)) ? null : i18n.t('aiTrainLib:autoStopLib.cond.number')
  }
  return c.value !== '' && c.value != null && String(c.value).trim() !== '' ? null : i18n.t('aiTrainLib:autoStopLib.cond.value')
}

// ── Khung thời gian ──────────────────────────────────────────────────────────

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function newSchedule(timezone = DEFAULT_TZ): AutoStopSchedule {
  return { repeat: 'weekly', days: [], timezone, start: '22:00', end: '06:00' }
}

export function scheduleProblem(s: AutoStopSchedule): string | null {
  const out: string[] = []
  if (s.repeat === 'weekly' && !s.days.length) out.push(i18n.t('aiTrainLib:autoStopLib.sch.weekday'))
  if (s.repeat === 'dates') {
    if (!s.dateFrom || !DATE_RE.test(s.dateFrom) || (s.dateTo && !DATE_RE.test(s.dateTo))) out.push(i18n.t('aiTrainLib:autoStopLib.sch.dates'))
    else if (s.dateTo && s.dateFrom > s.dateTo) out.push(i18n.t('aiTrainLib:autoStopLib.sch.dateOrder'))
  }
  if (!TIME_RE.test(s.start) || !TIME_RE.test(s.end)) out.push(i18n.t('aiTrainLib:autoStopLib.sch.times'))
  return out.length ? out.join(' · ') : null
}

/** Ghi chú cách hiểu khung giờ (qua nửa đêm / cả ngày). */
export function scheduleNote(s: AutoStopSchedule): string | null {
  if (!TIME_RE.test(s.start) || !TIME_RE.test(s.end)) return null
  if (s.start === s.end) return i18n.t('aiTrainLib:autoStopLib.note.allDay')
  if (s.start > s.end) return i18n.t('aiTrainLib:autoStopLib.note.overnight', { start: s.start, end: s.end })
  return null
}

const TZ_VI: Record<string, string> = {
  get 'Asia/Ho_Chi_Minh'() { return i18n.t('aiTrainLib:autoStopLib.tz.Ho_Chi_Minh') }, 'Asia/Bangkok': 'Bangkok', 'Asia/Jakarta': 'Jakarta', 'Asia/Singapore': 'Singapore',
  'Asia/Kuala_Lumpur': 'Kuala Lumpur', 'Asia/Manila': 'Manila', get 'Asia/Shanghai'() { return i18n.t('aiTrainLib:autoStopLib.tz.Shanghai') }, get 'Asia/Hong_Kong'() { return i18n.t('aiTrainLib:autoStopLib.tz.Hong_Kong') },
  get 'Asia/Taipei'() { return i18n.t('aiTrainLib:autoStopLib.tz.Taipei') }, get 'Asia/Tokyo'() { return i18n.t('aiTrainLib:autoStopLib.tz.Tokyo') }, get 'Asia/Seoul'() { return i18n.t('aiTrainLib:autoStopLib.tz.Seoul') }, get 'Asia/Phnom_Penh'() { return i18n.t('aiTrainLib:autoStopLib.tz.Phnom_Penh') },
  get 'Asia/Vientiane'() { return i18n.t('aiTrainLib:autoStopLib.tz.Vientiane') }, 'Asia/Yangon': 'Myanmar — Yangon', get 'Asia/Kolkata'() { return i18n.t('aiTrainLib:autoStopLib.tz.Kolkata') }, 'Asia/Dubai': 'Dubai',
  get 'Australia/Sydney'() { return i18n.t('aiTrainLib:autoStopLib.tz.Sydney') }, get 'Europe/London'() { return i18n.t('aiTrainLib:autoStopLib.tz.London') }, get 'Europe/Paris'() { return i18n.t('aiTrainLib:autoStopLib.tz.Paris') }, get 'Europe/Berlin'() { return i18n.t('aiTrainLib:autoStopLib.tz.Berlin') },
  get 'Europe/Moscow'() { return i18n.t('aiTrainLib:autoStopLib.tz.Moscow') }, get 'America/New_York'() { return i18n.t('aiTrainLib:autoStopLib.tz.New_York') }, get 'America/Los_Angeles'() { return i18n.t('aiTrainLib:autoStopLib.tz.Los_Angeles') }, get 'America/Chicago'() { return i18n.t('aiTrainLib:autoStopLib.tz.Chicago') },
  get UTC() { return i18n.t('aiTrainLib:autoStopLib.tz.UTC') },
}
const FALLBACK_TZ = Object.keys(TZ_VI)
const LEGACY_TZ = new Set(['Asia/Saigon', 'Asia/Calcutta', 'Asia/Rangoon', 'Asia/Katmandu', 'Europe/Kiev'])

/** Độ lệch so với UTC theo phút tại thời điểm `at`. */
export function tzOffsetMinutes(tz: string, at: Date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at)
    const g = (t: string) => Number(parts.find((p) => p.type === t)?.value)
    const asUtc = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'))
    return Math.round((asUtc - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000)
  } catch {
    return 0
  }
}

export function formatOffset(min: number): string {
  const sign = min < 0 ? '-' : '+'
  const a = Math.abs(min)
  return `GMT${sign}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`
}

/** Tên thân thiện: "(GMT+07:00) Việt Nam — Hồ Chí Minh, Hà Nội". */
export function timezoneLabel(tz: string, at: Date = new Date()): string {
  const name = TZ_VI[tz] ?? tz.split('/').slice(1).join(' / ').replace(/_/g, ' ') ?? tz
  return `(${formatOffset(tzOffsetMinutes(tz, at))}) ${name || tz}`
}

/** Danh sách múi giờ (tên thân thiện, xếp theo độ lệch), múi phổ biến ở Đông Nam Á lên đầu. */
export function timezoneOptions(at: Date = new Date()): Array<{ value: string; label: string; offset: number }> {
  const pinned = ['Asia/Ho_Chi_Minh', 'Asia/Bangkok', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul']
  let all: string[] = FALLBACK_TZ
  try {
    const sv = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf
    // ICU có thể dùng tên cũ (Asia/Saigon, Asia/Calcutta…) — luôn có tên chuẩn của danh sách quen thuộc, bỏ bí danh cũ.
    if (sv) all = [...new Set([...FALLBACK_TZ, ...sv('timeZone').filter((z) => !LEGACY_TZ.has(z))])]
  } catch { /* trình duyệt cũ */ }
  const rows = all.map((value) => ({ value, label: timezoneLabel(value, at), offset: tzOffsetMinutes(value, at) }))
  const rank = (v: string) => { const i = pinned.indexOf(v); return i < 0 ? pinned.length : i }
  rows.sort((a, b) => rank(a.value) - rank(b.value) || a.offset - b.offset || a.label.localeCompare(b.label))
  return rows
}

export function searchTimezones<T extends { value: string; label: string }>(list: T[], q: string): T[] {
  const k = fold(q)
  return k ? list.filter((x) => fold(x.label).includes(k) || fold(x.value).includes(k)) : list
}

// ── Kiểm lỗi cả hộp ──────────────────────────────────────────────────────────

export type AutoStopErrors = {
  general?: string
  /** Lỗi từng điều kiện: khoá `${nhóm}:${điều kiện}`. */
  conditions: Record<string, string>
  schedules: Record<number, string>
  closing?: string
}

/** Bỏ nhóm rỗng trước khi lưu. */
export function cleanAutoStop(a: AutoStopSettings): AutoStopSettings {
  return { ...a, closingMessage: a.closingMessage.trim(), conditionGroups: a.conditionGroups.filter((g) => g.conditions.length > 0) }
}

export function validateAutoStop(a: AutoStopSettings): AutoStopErrors {
  const e: AutoStopErrors = { conditions: {}, schedules: {} }
  a.conditionGroups.forEach((g, gi) => g.conditions.forEach((c, ci) => {
    const p = stopConditionProblem(c)
    if (p) e.conditions[`${gi}:${ci}`] = p
  }))
  a.schedules.forEach((s, i) => { const p = scheduleProblem(s); if (p) e.schedules[i] = p })
  if (a.closingMessage.length > AUTO_STOP_LIMITS.closing) e.closing = i18n.t('aiTrainLib:autoStopLib.err.closing', { max: AUTO_STOP_LIMITS.closing })
  const hasCond = a.conditionGroups.some((g) => g.conditions.length > 0)
  if (a.enabled && !hasCond && !a.schedules.length) e.general = i18n.t('aiTrainLib:autoStopLib.err.general')
  return e
}

export function hasErrors(e: AutoStopErrors): boolean {
  return !!e.general || !!e.closing || Object.keys(e.conditions).length > 0 || Object.keys(e.schedules).length > 0
}

/** Dòng tóm tắt dưới tiêu đề hàng cài đặt. */
export function autoStopSummary(a: AutoStopSettings): string {
  const g = a.conditionGroups.filter((x) => x.conditions.length).length
  const parts = [g ? i18n.t('aiTrainLib:autoStopLib.summary.groups', { count: g }) : '', a.schedules.length ? i18n.t('aiTrainLib:autoStopLib.summary.schedules', { count: a.schedules.length }) : ''].filter(Boolean)
  return parts.join(' · ') || i18n.t('aiTrainLib:autoStopLib.summary.none')
}
